<template>
    <footer
        class="md-footer layout3"
        :class="[{ inner: inner }]"
        :style="{
            ...colorStyle,
            ...spaceStyle,
            background: bgColor
        }"
    >
        <div class="f-col footer-container">
            <div class="site-logo" v-if="logos.length > 0">
                <template v-for="(logo, index) in validLogos">
                    <a
                        v-if="logo.logo.href"
                        :key="index"
                        :href="logo.logo.href"
                        aria-label="site logo"
                        rel="noopener noreferrer"
                    >
                        <site-img
                            class="logo-item"
                            :src="logo.logo"
                            alt="site logo"
                            :style="{ height: size / 100 + 'em' }"
                            :ssrInitSize="30"
                            dir="height"
                        />
                    </a>
                    <site-img
                        v-else
                        class="logo-item"
                        :key="'key-' + index"
                        :src="logo.logo"
                        alt="site logo"
                        :style="{ height: size / 100 + 'em' }"
                        :ssrInitSize="30"
                        dir="height"
                    />
                </template>
            </div>
            <div class="top-content">
                <div class="left-content">
                    <ul class="main-menu">
                        <li class="first-col">
                            <menu-item
                                v-for="(menu, index) in firstMenus"
                                :key="'firstMenu-' + index"
                                :selfKey="'firstMenu-' + index"
                                :expandKey="expandKey"
                                :menu="menu"
                                :useMenuStyle="footerMenuStyle"
                                :useSubMenuStyle="footerSubMenuStyle"
                                @updateExpandKey="updateExpandKey"
                            />
                        </li>
                        <li class="second-col">
                            <menu-item
                                v-for="(menu, index) in secondMenus"
                                :key="'secondMenu-' + index"
                                :selfKey="'secondMenu-' + index"
                                :expandKey="expandKey"
                                :menu="menu"
                                :useMenuStyle="footerMenuStyle"
                                :useSubMenuStyle="footerSubMenuStyle"
                                @updateExpandKey="updateExpandKey"
                            />
                        </li>
                        <li class="third-col">
                            <menu-item
                                v-for="(menu, index) in thirdMenus"
                                :key="'thirdMenu-' + index"
                                :selfKey="'thirdMenu-' + index"
                                :expandKey="expandKey"
                                :menu="menu"
                                :useMenuStyle="footerMenuStyle"
                                :useSubMenuStyle="footerSubMenuStyle"
                                @updateExpandKey="updateExpandKey"
                            />
                        </li>
                    </ul>
                </div>
                <div class="right-content">
                    <div class="detail">
                        <div
                            v-if="companyInfo?.name"
                            class="co-name"
                            :style="footerBroNameStyle"
                            v-html="companyInfo.name"
                        ></div>
                        <div
                            v-if="
                                footerAgentInfo?.firstName ||
                                    footerAgentInfo?.lastName
                            "
                            class="agent-name"
                            :style="{
                                ...footerAgentInfoStyle
                            }"
                        >
                            {{ footerAgentInfo.firstName || '' }}
                            {{ footerAgentInfo.lastName || '' }}
                        </div>
                        <p class="agent-info">
                            <span
                                v-if="footerAgentInfo?.phone"
                                class="detail-phone"
                                :style="footerAgentInfoStyle"
                            >
                                <a
                                    :href="'tel:' + footerAgentInfo.phone"
                                    target="_top"
                                    rel="noopener noreferrer"
                                >
                                    {{ $ft.formatPhone(footerAgentInfo.phone) }}
                                </a>
                            </span>
                            <span
                                v-if="footerAgentInfo?.email"
                                class="detail-email"
                                :style="footerAgentInfoStyle"
                            >
                                <a
                                    :href="'mailto:' + footerAgentInfo.email"
                                    rel="noopener noreferrer"
                                >
                                    {{ footerAgentInfo.email }}
                                </a>
                            </span>
                        </p>
                        <div
                            v-if="getAddressInfo"
                            class="detail-addr site-p"
                            :style="footerBroAddrStyle"
                            v-html="getAddressInfo"
                        ></div>
                    </div>

                    <div class="detail subdomain-detail" v-if="subOwnerInfo">
                        <p class="detail-info subinfo-name" v-if="subOwnerName">
                            <span v-text="subOwnerName" class="detail-name">
                            </span>
                        </p>
                        <p class="detail-info">
                            <span
                                v-if="subOwnerInfo?.phone"
                                class="detail-phone"
                            >
                                <a
                                    :href="'tel:' + subOwnerInfo.phone"
                                    target="_top"
                                    rel="noopener noreferrer"
                                >
                                    {{ $ft.formatPhone(subOwnerInfo.phone) }}
                                </a>
                            </span>
                            <span
                                v-if="subOwnerInfo?.email"
                                class="detail-email"
                            >
                                <a
                                    :href="'mailto:' + subOwnerInfo.email"
                                    rel="noopener noreferrer"
                                >
                                    {{ subOwnerInfo.email }}
                                </a>
                            </span>
                        </p>
                    </div>

                    <div
                        v-if="socialNetworksInfo.length > 0"
                        class="share-links"
                    >
                        <a
                            v-for="(social, index) in socialNetworksInfo"
                            :key="social.id + '-' + index"
                            class="share-item"
                            :href="social.url"
                            :aria-label="social.socialNetwork"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i class="iconfont" :class="social.icon"></i>
                        </a>
                    </div>

                    <div class="detail other-cont">
                        <template v-if="footerAgentCustomContent">
                            <div
                                class="custom-content"
                                v-html="
                                    formatFontSize(footerAgentCustomContent)
                                "
                            ></div>
                        </template>
                    </div>
                    <div class="detail-info">
                        <template v-if="agentHasDisclaimer">
                            <p
                                v-for="(
                                    file, index
                                ) in footerAgentInfo.disclaimerList"
                                :key="index + 'mainfile'"
                                class="detail-file"
                            >
                                <a :href="file.url" rel="noopener noreferrer">
                                    {{ file.name.split('.')[0] }}
                                </a>
                            </p>
                        </template>
                        <!-- sub file -->
                        <template v-if="subOwnerHasDisclaimer">
                            <p
                                v-for="(
                                    file, index
                                ) in subOwnerInfo.disclaimerList"
                                :key="index + 'subfile'"
                                class="detail-file"
                            >
                                <a :href="file.url" rel="noopener noreferrer">
                                    {{ file.name.split('.')[0] }}
                                </a>
                            </p>
                        </template>
                    </div>
                </div>
            </div>

            <div class="bottom-content">
                <closely-app-download
                    useModule="md-footer"
                    :downloadTheme="true"
                />
                <a class="md-footer_notshare" v-if="isDoNotShareShow" href="/site/dsar">{{ $t('md-footer.doNotShare') }} --> </a>
                <div v-if="!hideCopyRight" class="copyright">
                    <template v-if="gfFlag != '1'">
                        <p
                            class="cr"
                            v-if="showCopyright"
                            v-html="
                                $t('md-footer.madeInAndCopyright', {
                                    chimeLink,
                                    year
                                })
                            "
                        ></p>
                        <p class="ppt">
                            <CopyrightLink :gfFlag="gfFlag" :isShowAccessibility="isShowAccessibility" /> 
                        </p>
                    </template>
                    <p v-else class="cr">
                        {{ $t('md-footer.geographicFarmingLlc', { year }) }}
                        <CopyrightLink :gfFlag="gfFlag" :isShowAccessibility="isShowAccessibility" /> 
                    </p>
                    <div class="line"></div>
                </div>

                <MlsDisclaimer
                    v-if="!wordPressSSR"
                    theme="layout3"
                    @update="mlsUpdated"
                />

                <div class="no-mls-disclaimer texas-mls" v-if="hasTeaxsMls">
                    <a
                        href="https://www.trec.texas.gov/sites/default/files/pdf-forms/CN%201-3.pdf"
                        target="_blank"
                        :title="$t('md-footer.realEstateConsumer')"
                        rel="noopener noreferrer"
                    >{{ $t('md-footer.realEstateConsumer') }}</a>
                </div>
                <div
                    class="no-mls-disclaimer"
                    v-if="noMlsDisclaimers.length > 0"
                >
                    <div
                        class="claimer"
                        v-for="(noMls, index) in noMlsDisclaimers"
                        :key="index"
                        v-html="noMls.label"
                    ></div>
                </div>
            </div>
        </div>
    </footer>
</template>

<script>
import common from '../index-layout';
export default {
    mixins: [common],
    data: function() {
        return {};
    },
    computed: {
        firstMenus() {
            return this.z_menus.filter((item, index) => index % 3 === 0);
        },
        secondMenus() {
            return this.z_menus.filter((item, index) => index % 3 === 1);
        },
        thirdMenus() {
            return this.z_menus.filter((item, index) => index % 3 === 2);
        }
    }
};
</script>

<style lang="scss">
@import '../../../theme/media.scss';
@import '../../../theme/mixin.scss';
@import '../index.scss';

.md-footer.layout3 {
    background: rgba(25, 25, 25, 1);
    color: var(--g-primary-color);

    &.fixed {
        position: fixed;
        bottom: 0;
        width: 100%;
    }
    .footer-container {
        position: relative;
    }
    .site-logo {
        font-size: 50px;
        position: relative;
        margin-bottom: 50px;
        .logo-item {
            margin-right: 40px;
            height: 1em;
            vertical-align: top;
        }
    }
    .top-content {
        display: flex;
        column-gap: 90px;
        .left-content {
            width: 54%;

            .main-menu {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                align-items: flex-start;

                .first-col,
                .second-col,
                .third-col {
                    width: 33.3%;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;

                    .menu-item {
                        cursor: pointer;
                        display: block;
                        padding-left: 0;
                        color: var(--g-primary-color);

                        & > a {
                            display: flex;
                            align-items: center;
                            position: relative;
                            max-width: 100%;
                            @include ellipsis();
                        }

                        .footer-menu {
                            line-height: 2.15;
                            cursor: pointer;
                            font-family: var(--font-medium) !important;

                            .icon-arrow-down {
                                font-size: inherit;
                                position: relative;
                                left: 4px;
                                display: inline-block;
                                transform: scale(0.7, 0.8);
                            }

                            &.down .icon-arrow-down {
                                transform: rotate(180deg) scale(0.7, 0.8);
                            }
                        }

                        .footer-submenu {
                            display: flex;
                            flex-direction: column;
                            gap: 20px;
                            padding-top: 10px;
                            .sub-menu {
                                margin-bottom: 0;
                                display: block;
                                line-height: 1;

                                & > a {
                                    display: inline-block;
                                    position: relative;
                                    font-weight: 400;
                                    font-size: 14px;
                                    // &:before {
                                    //     content: ' ';
                                    //     position: absolute;
                                    //     left: 0%;
                                    //     right: unset;
                                    //     bottom: 2px;
                                    //     top: unset;
                                    //     height: 1px;
                                    //     width: 0%;
                                    //     background-color: var(
                                    //         --g-primary-color
                                    //     );
                                    //     -webkit-transition: width 0.3s
                                    //         ease-in-out;
                                    //     transition: width 0.3s ease;
                                    // }
                                    // &:hover:before {
                                    //     width: 100%;
                                    // }
                                }
                            }
                        }
                    }
                }
            }
        }

        .right-content {
            flex: 1;

            .detail {
                .co-name,
                .agent-name {
                    font-size: 16px;
                    line-height: 20px;
                    text-align: left;
                    color: var(--g-primary-color);
                    font-family: var(--font-medium) !important;
                }
                .agent-name {
                    margin-top: 20px;
                }

                .agent-info {
                    margin-top: 10px;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    font-family: var(--font-normal);
                    color: var(--g-text-color);

                    .detail-phone {
                        margin-right: 24px;
                    }

                    > span {
                        vertical-align: top;
                    }
                }

                .detail-addr {
                    margin-top: 10px;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    font-family: var(--font-normal);
                    color: var(--g-text-color);
                }

                &.subdomain-detail,
                &.other-cont {
                    margin-top: 10px;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    color: var(--g-text-color);
                }
                &.subdomain-detail {
                    .subinfo-name {
                        margin-bottom: 10px;
                    }
                    .detail-phone {
                        margin-right: 24px;
                    }
                    > span {
                        vertical-align: top;
                    }
                }
                &.other-cont {
                    margin-top: 20px;
                }
            }

            .share-links {
                margin-top: 20px;
                font-size: 0px; // remove inline-block spacing
                display: flex;
                flex-wrap: wrap;
                gap: 10px;

                .share-item {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    width: 30px;
                    height: 30px;
                    .iconfont {
                        font-size: 20px;
                        color: var(--g-tip-color);
                    }
                    &:hover {
                        .iconfont {
                            color: var(--g-primary-color);
                        }
                    }
                }
            }

            > .detail-info {
                margin-top: 20px;
                color: var(--g-text-color);
                .detail-file {
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                }
            }

            @include no-mls-disclaimer;
        }
    }

    .bottom-content {
        margin-top: 50px;
        .md-footer_notshare{
            color: var(--g-tip-color);
            margin-bottom: 10px;
            display: block;
        }
        .download-closely {
            margin-bottom: 40px;
            display: flex;
            flex-direction: column-reverse;
            width: 100px;
            align-items: center;
            gap: 10px;
            .code-title {
                font-size: 12px;
                font-weight: 400;
                line-height: 21px;
                text-align: left;
                color: var(--g-primary-color);
            }
            .code-wrapper {
                .android {
                    margin-left: 30px;
                }

                .android,
                .ios {
                    img {
                        width: 80px;
                        height: 80px;
                    }
                }
                .qr-code {
                    border-radius: 0;
                }

                justify-content: flex-start;
            }
        }

        .copyright {
            font-size: 12px;
            font-weight: 400;
            line-height: 20px;
            text-align: left;
            color: var(--g-tip-color);

            .cr {
                line-height: 20px;

                a {
                    text-decoration: underline;
                }
            }

            .ppt {
                margin-top: 10px;
                line-height: 20px;
                a {
                    white-space: nowrap;
                }
            }

            .line {
                width: 100%;
                height: 1px;
                background: rgba(218, 218, 218, 0.5);
                margin: 40px 0;
            }
        }

        .detail-info {
            margin-top: 10px;

            .detail-file {
                cursor: pointer;
                font-size: 14px;
                color: var(--g-tip-color);
            }
        }
        @include no-mls-disclaimer;

        .mls-declaimer-container-v2 {
            padding-top: 0;
            .declaimer {
                display: flex;
                flex-direction: column-reverse;
                gap: 15px;
                align-items: flex-start;
                .content {
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    color: var(--g-tip-color);
                }
                .mls-logo {
                    margin-top: 0;
                }
            }
        }
    }

    .fair-house-logo {
        vertical-align: text-bottom;
        margin: 0 5px;
        padding-top: 5px;
    }

    &.inner {
        .top-content {
            flex-direction: column;
            row-gap: 50px;

            .left-content {
                width: 100%;
            }
            .right-content {
                flex: none;
                width: 100%;
            }
        }
    }

    @mixin media-600-footer {
        .site-logo {
            margin-bottom: 25px;
        }
        .top-content {
            flex-direction: column;

            .left-content {
                width: 100%;
                display: none;
            }
        }

        .bottom-content {
            margin-top: 20px;

            .download-closely {
                margin-bottom: 20px;
                flex-direction: column;
                width: 100%;
                align-items: flex-start;
                .code-title {
                    margin-bottom: 0;
                }
            }

            .copyright {
                .line {
                    margin: 20px 0;
                }
            }
        }
    }

    @include media-600 {
        @include media-600-footer();
    }
}
</style>
